/**
 * useDismiss — закрытие всплывающих слоёв по Esc и клику вне контейнера.
 * Общая логика для Popover/Dialog/Drawer/DatePicker (доступность: Esc всегда закрывает).
 */

import { useEffect, useRef, type RefObject } from 'react';

export interface DismissOptions {
  enabled: boolean;
  onDismiss: () => void;
  /** Контейнер(ы), клик внутри которых НЕ закрывает слой. */
  refs: Array<RefObject<HTMLElement>>;
  /** Закрывать по клику вне (default true). */
  closeOnOutside?: boolean;
  /** Закрывать по Escape (default true). */
  closeOnEscape?: boolean;
}

export function useDismiss({
  enabled,
  onDismiss,
  refs,
  closeOnOutside = true,
  closeOnEscape = true,
}: DismissOptions): void {
  // Держим актуальные onDismiss/refs в ref, чтобы эффект не переподписывался на каждый рендер
  // (иначе новый литерал [ref] пересоздаёт слушатели постоянно).
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;
  const refsRef = useRef(refs);
  refsRef.current = refs;

  useEffect(() => {
    if (!enabled) return;

    const onPointerDown = (e: MouseEvent): void => {
      if (!closeOnOutside) return;
      const target = e.target as Node | null;
      if (!target) return;
      for (const ref of refsRef.current) {
        if (ref.current && ref.current.contains(target)) return;
      }
      onDismissRef.current();
    };

    const onKeyDown = (e: KeyboardEvent): void => {
      if (closeOnEscape && e.key === 'Escape') {
        e.stopPropagation();
        onDismissRef.current();
      }
    };

    document.addEventListener('mousedown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [enabled, closeOnOutside, closeOnEscape]);
}
